import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldSetStatus } from "../../form/Form";
import { getPageContext } from "../pageContext";
import { applyUpdates, revertChanges } from "./apply";
import { allChanges, ChangeLedger, changesFrom, changesOfForm, changesOfMessage, countByForm, dropChanges, recordChanges } from "./changeLedger";
import { discoverForms, findForm } from "./discovery";
import { watchKitSubmit } from "./formStatus";
import { formTitle } from "./labels";
import { clearMarkers, isApplying, reconcileMarkers, supersededChanges } from "./markers";
import { armSaveOriginFor, disarmSaveOrigin } from "./saveOrigin";
import { buildSnapshot, routeOf } from "./snapshot";
import type { AppliedChange, ApplyResult, DiscoveredForm, FormUpdate, PageForms, RevertResult } from "./types";
import "./FormBridge.scss";

const CONTENT_ROOT_SELECTOR = ".content-page";

/** How many fields the assistant has changed on one form and the title the chat shows for it. */
export interface FormChangeCount {
  formId: string;
  title: string;
  count: number;
}

export interface FormBridge {
  /** The forms on the page for the next chat turn, or undefined when the page renders none. */
  snapshot(): PageForms | undefined;
  /** Applies a reply's field updates and remembers them under the assistant message's index for revert. */
  applyReply(formUpdates: FormUpdate[] | undefined, messageIndex: number): ApplyResult;
  /**
   * Puts back every field one assistant message changed. A field the assistant re-set in a later
   * message goes back through that change as well, so it ends where it began; earlier messages
   * keep their values.
   */
  revertMessage(messageIndex: number): void;
  /** Puts back every field the assistant changed on one form, across messages. */
  revertForm(formId: string): void;
  /** Title of the form the admin last focused (or the first form of the last snapshot) — the "Forms:" chip. */
  activeFormTitle: string | undefined;
  /** Live per-form change counts, for the "AI changed N fields in …" lines. */
  changeCounts: FormChangeCount[];
}

const EMPTY_RESULT: ApplyResult = { applied: [], rejected: [] };

function titleOf(form: HTMLFormElement, root: ParentNode): string {
  const forms = Array.from(root.querySelectorAll("form"));
  return formTitle(form, forms.indexOf(form), root);
}

function formOf(event: Event): HTMLFormElement | null {
  return event.target instanceof Element ? event.target.closest("form") : null;
}

function fieldKey(change: AppliedChange): string {
  return `${change.formId}\u0000${change.path}`;
}

interface SubmitTracking {
  /** Running kit-status watchers by form id, so a resubmit replaces the earlier watch. */
  watchers: Map<string, () => void>;
  forget: (changes: AppliedChange[]) => void;
}

/** A submitted form's ledger id — a kit form's own id, else the id discovery gives it — and whether it is a kit form. */
type SubmittedForm = Pick<DiscoveredForm, "formId" | "kit">;

function submittedForm(form: HTMLFormElement, root: ParentNode): SubmittedForm | undefined {
  const kitFormId = form.getAttribute("data-form-id");
  if (kitFormId) {
    return { formId: kitFormId, kit: true };
  }
  return findForm(discoverForms(root), form);
}

// Only what was on the form when it was submitted is saved by this submit; later changes stay
// until a submit of their own. A framework form gives no success signal, so its submit is taken
// as the save.
function trackSubmit({ formId, kit }: SubmittedForm, submitted: AppliedChange[], tracking: SubmitTracking): void {
  if (!kit) {
    tracking.forget(submitted);
    return;
  }
  tracking.watchers.get(formId)?.();
  const stop = watchKitSubmit(formId, (status) => {
    tracking.watchers.delete(formId);
    if (status !== FieldSetStatus.SUBMITTING) {
      disarmSaveOrigin();
    }
    if (status === FieldSetStatus.SUBMIT_SUCCESS) {
      tracking.forget(submitted);
    }
  });
  tracking.watchers.set(formId, stop);
}

/**
 * The page-forms side of the AI chat: snapshots the forms under `.content-page` for each turn,
 * applies the assistant's updates with markers, and reverts them on request. While enabled it
 * listens (capture phase) for focus, edits and submits on the page so markers track what the
 * admin does; disabled, it attaches nothing and returns null, so the chat behaves as before.
 *
 * A change is remembered only while the field still holds the assistant's value: once the admin
 * types over it, the record goes with the marker, so counts show live changes and a revert can
 * never undo the admin's own edit.
 *
 * Hook-local state only: the page's DOM does not survive a reload, so neither do the changes.
 */
export function useFormBridge(enabled: boolean): FormBridge | null {
  const rootRef = useRef<HTMLElement | null>(null);
  const interactions = useRef(new WeakMap<HTMLFormElement, number>());
  const interactionCounter = useRef(0);
  const ledger = useRef<ChangeLedger>(new Map());
  const titles = useRef(new Map<string, string>());
  const [activeFormTitle, setActiveFormTitle] = useState<string>();
  const [changeCounts, setChangeCounts] = useState<FormChangeCount[]>([]);

  const rememberTitles = useCallback((forms: DiscoveredForm[]): void => {
    forms.forEach((form) => titles.current.set(form.formId, form.title));
  }, []);

  const refreshCounts = useCallback((): void => {
    const counts = Array.from(countByForm(ledger.current).entries());
    setChangeCounts(counts.map(([formId, count]) => ({ formId, title: titles.current.get(formId) ?? formId, count })));
  }, []);

  const reconcile = useCallback((): void => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    const forms = discoverForms(root);
    const superseded = supersededChanges(allChanges(ledger.current), forms);
    if (superseded.length > 0) {
      dropChanges(ledger.current, superseded);
      refreshCounts();
    }
    reconcileMarkers(allChanges(ledger.current), root, forms);
  }, [refreshCounts]);

  const forget = useCallback(
    (changes: AppliedChange[]): void => {
      dropChanges(ledger.current, changes);
      reconcile();
      refreshCounts();
    },
    [reconcile, refreshCounts],
  );

  // A hidden field (its form is here, the input is not rendered) still holds the value in the
  // form's model, so its record stays and re-marks it when it renders again — the same rule
  // reconcile follows. Everything else was put back, overridden by the admin, or lost its form.
  const forgetReverted = useCallback(
    (result: RevertResult): void => {
      forget([...result.reverted, ...result.superseded, ...result.missing]);
    },
    [forget],
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const root = document.querySelector<HTMLElement>(CONTENT_ROOT_SELECTOR);
    if (!root) {
      return undefined;
    }
    rootRef.current = root;
    const changes = ledger.current;
    const tracking: SubmitTracking = { watchers: new Map(), forget };
    let pendingReconcile: ReturnType<typeof setTimeout> | undefined;

    const onFocusIn = (event: Event): void => {
      const form = formOf(event);
      if (form) {
        interactionCounter.current += 1;
        interactions.current.set(form, interactionCounter.current);
        setActiveFormTitle(titleOf(form, root));
      }
    };
    // Capture listeners run before React's own handler has moved the edit into the form's
    // model, so the reconcile waits a macrotask — after React's synchronous flush of the event.
    const onEdit = (): void => {
      if (isApplying() || pendingReconcile !== undefined) {
        return;
      }
      pendingReconcile = setTimeout(() => {
        pendingReconcile = undefined;
        reconcile();
      }, 0);
    };
    // Reconciled first, so records of values the admin has since overwritten no longer count; a
    // record of a field that is not rendered stays, its value still goes out from the model.
    const onSubmit = (event: Event): void => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      reconcile();
      const target = submittedForm(form, root);
      if (target) {
        const submitted = changesOfForm(changes, target.formId);
        armSaveOriginFor(submitted);
        trackSubmit(target, submitted, tracking);
      }
    };

    root.addEventListener("focusin", onFocusIn, true);
    root.addEventListener("input", onEdit, true);
    root.addEventListener("change", onEdit, true);
    root.addEventListener("submit", onSubmit, true);
    return (): void => {
      root.removeEventListener("focusin", onFocusIn, true);
      root.removeEventListener("input", onEdit, true);
      root.removeEventListener("change", onEdit, true);
      root.removeEventListener("submit", onSubmit, true);
      if (pendingReconcile !== undefined) {
        clearTimeout(pendingReconcile);
      }
      tracking.watchers.forEach((stop) => stop());
      clearMarkers(root);
      changes.clear();
      rootRef.current = null;
      setChangeCounts([]);
      setActiveFormTitle(undefined);
    };
  }, [enabled, reconcile, forget]);

  const snapshot = useCallback((): PageForms | undefined => {
    const root = rootRef.current;
    if (!root) {
      return undefined;
    }
    const forms = discoverForms(root);
    if (forms.length === 0) {
      setActiveFormTitle(undefined);
      return undefined;
    }
    reconcile();
    rememberTitles(forms);
    const pathname = window.location.pathname;
    const pageContext = getPageContext(pathname);
    const result = buildSnapshot(forms, {
      route: routeOf(pathname),
      entityType: pageContext?.entityType,
      entityId: pageContext?.entityId,
      interactionRank: (form) => interactions.current.get(form.element) ?? 0,
    });
    setActiveFormTitle(result ? result.forms[0].title : undefined);
    return result;
  }, [reconcile, rememberTitles]);

  const applyReply = useCallback(
    (formUpdates: FormUpdate[] | undefined, messageIndex: number): ApplyResult => {
      const root = rootRef.current;
      if (!root || !formUpdates || formUpdates.length === 0) {
        return EMPTY_RESULT;
      }
      rememberTitles(discoverForms(root));
      const result = applyUpdates(formUpdates, root);
      if (result.applied.length > 0) {
        recordChanges(ledger.current, messageIndex, result.applied);
        refreshCounts();
      }
      return result;
    },
    [rememberTitles, refreshCounts],
  );

  const revertMessage = useCallback(
    (messageIndex: number): void => {
      const root = rootRef.current;
      const own = changesOfMessage(ledger.current, messageIndex);
      if (!root || own.length === 0) {
        return;
      }
      const touched = new Set(own.map(fieldKey));
      // Later messages that re-set one of these fields are undone too (the restore would
      // otherwise be skipped as superseded); earlier ones keep their value.
      const chain = changesFrom(ledger.current, messageIndex).filter((change) => touched.has(fieldKey(change)));
      forgetReverted(revertChanges(chain, root));
    },
    [forgetReverted],
  );

  const revertForm = useCallback(
    (formId: string): void => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      forgetReverted(revertChanges(changesOfForm(ledger.current, formId), root));
    },
    [forgetReverted],
  );

  return useMemo(
    () => (enabled ? { snapshot, applyReply, revertMessage, revertForm, activeFormTitle, changeCounts } : null),
    [enabled, snapshot, applyReply, revertMessage, revertForm, activeFormTitle, changeCounts],
  );
}
